import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../common/components/base_form_controlled_modal";
import { DefaultFormMultiAutocomplete } from "../../../../common/components/default_form_multi_autocomplete";
import { FormDatePickerInput } from "../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { ReportMonitoramentoChecklistForm, ReportMonitoramentoChecklistSchema } from "../schemas/Obras_informacoesgerais/report_monitoramento_checklist";
import { zodResolver } from "@hookform/resolvers/zod";
import { ObraAreaDropDown } from "../../domain/models/obra_area_dropdown";
import { ObraServicoDropDown } from "../../domain/models/obra_servico_dropdown";
import { MonitoramentoChecklistEmpresa } from "../../domain/models/monitoramento_checklist_empresa";
import { MonitoramentoChecklistFuncionario } from "../../domain/models/monitoramento_checklist_funcionario";

import FormContainer from "../../../../common/components/form_container";

/**
 * ReportMonitoramentoChecklistModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  obraAreasDropDown: ObraAreaDropDown[],
  obraServicosDropDown: ObraServicoDropDown[],
  monitoramentoChecklistEmpresas: MonitoramentoChecklistEmpresa[],
  monitoramentoChecklistFuncionarios: MonitoramentoChecklistFuncionario[],
  onClose: (openState: boolean) => void;
  onSubmit: (form: ReportMonitoramentoChecklistForm) => void;
};

export const ReportMonitoramentoChecklistModal = ({
  open,
  saving,
  loading,
  obraAreasDropDown,
  obraServicosDropDown,
  monitoramentoChecklistEmpresas,
  monitoramentoChecklistFuncionarios,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<ReportMonitoramentoChecklistForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ReportMonitoramentoChecklistSchema),
  });

  const statusArea = useMemo(() => {
    return [
      { id: 0, descricao: 'Em Aberto' },
      { id: 1, descricao: 'Finalizadas' },
    ];
  }, [
  ]);

  const statusServico = useMemo(() => {
    return [
      { id: 0, descricao: 'Em Aberto' },
      { id: 1, descricao: 'Finalizadas' },
    ];
  }, [
  ]);

  const statusVerificacao = useMemo(() => {
    return [
      { id: 0, descricao: 'Verificação Não Iniciada' },
      { id: 3, descricao: 'Verificação Iniciada' },
      { id: 1, descricao: 'Encerrada' },
      { id: 2, descricao: 'Aprovado' },
    ];
  }, [
  ]);

  const statusItemChecklist = useMemo(() => {
    return [
      { id: 0, descricao: 'Não se aplica' },
      { id: 3, descricao: 'Retrabalho' },
      { id: 1, descricao: 'Reprovado' },
      { id: 2, descricao: 'Aprovado' },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      hookForm.reset();
    }
  }, [
    open,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title='Relatório Monitoramento de Checklist'
      confirmButtonLabel="Gerar relatório"
      confirmSavingButtonLabel="Gerando relatório..."
      savingLabel="Gerando"
      width="45rem"
      onClose={() => onClose(false)}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title={"Informações"} arrangement="fullRow">
        <>
          <DefaultFormMultiAutocomplete
            name='area'
            label='Área'
            options={obraAreasDropDown}
            getOptionKey={x => x.idObraAreaChecklist}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormMultiAutocomplete
            name='statusArea'
            label='Status da Área'
            options={statusArea}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormMultiAutocomplete
            name='servico'
            label='Serviço'
            chipMaxLength={50}
            options={obraServicosDropDown}
            getOptionKey={x => x.idChecklistServico}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormMultiAutocomplete
            name='statusServico'
            label='Status do Serviço'
            options={statusServico}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <div className="w-full flex gap-1">
            <FormDatePickerInput
              name='dataAberturaDe'
              label='Abertura de'
              reactHookForm={hookForm}
            />

            <FormDatePickerInput
              name='dataAberturaAte'
              label='Abertura Ate'
              reactHookForm={hookForm}
            />

          </div>

          <div className="w-full flex gap-1">
            <FormDatePickerInput
              name='dataEncerramentoDe'
              label='Encerramento De'
              reactHookForm={hookForm}
            />

            <FormDatePickerInput
              name='dataEncerramentoAte'
              label='Encerramento Ate'
              reactHookForm={hookForm}
            />

          </div>

          <DefaultFormMultiAutocomplete
            name='statusVerificacao'
            label='Status do Verificação'
            options={statusVerificacao}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormMultiAutocomplete
            name='statusItemChecklist'
            label='Status Item Checklist'
            options={statusItemChecklist}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormMultiAutocomplete
            name='empresaTercerizada'
            label='Empresas terceirizadas'
            options={monitoramentoChecklistEmpresas}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormMultiAutocomplete
            name='funcionarios'
            label='Funcionarios'
            options={monitoramentoChecklistFuncionarios}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}